import IApiCaller, {
  IArticlesResponse,
  ICompaniesSummaryResponse,
  ISearchCompaniesResponse,
  IScoredCompaniesResponse,
  IThemesResponse,
  AlertTypeKey,
  IDigestAlertPreference,
  ISmartAlertPreference,
  ICustomAlertPreference,
  IUserEmailPreferencesResponse,
  IUserListCreate,
  IUserListAddOrRemoveCompany,
  IUserListDelete,
  IUsersEmailPreferences,
  ICustomAlertId,
} from '../types/api-caller';
import axios from 'axios';
import { API_BASE, API_ROUTES, getThemesPath } from '../constants/api';
import ICompany, { ICompanyScoreHistory } from '../types/company';
import { IToScoreCompany, IScoredUpload, IScoredUploadResponse } from '../types/scoring-company';
import IEmail from '../types/email';
import { IArticleWithDowJones } from '../types/article';
import { IContactDefaults, IContactRequest } from '../types/contact';
import { IValidateUserResponse, IPrivacyConsentRequest } from '../types/admin';
import IConfig from '../types/config';
import { getTokenFromLocalStorage } from '../utils/local-storage';
import { IUserData, UserInfo } from '../types/user-info';
import { IMetricSeries, ILoginAttempts, IPipelineStatusResponse } from '../types/dashboard';

export default function useApiCaller(): IApiCaller {
  const ARTICLES_PER_PAGE = 50;

  /**
   * Setup
   */
  const requestConfig = { headers: { Authorization: `Bearer ${getTokenFromLocalStorage()}` } };
  const apiClient = axios.create({
    baseURL: API_BASE,
  });

  /**
   * Helpers
   */
  const _get = async (path: string, config = {}) => {
    const response = await apiClient.get(path, { ...requestConfig, ...config });
    return response.data;
  };

  const _post = async (path: string, params: { [x: string]: any } = {}) => {
    const response = await apiClient.post(path, params, requestConfig);
    return response.data;
  };

  /* _put is not supported by some clients including Lloyds  use post
  const _put = async (path: string, params: { [x: string]: any } = {}) => {
    const response = await apiClient.put(path, params, requestConfig);
    return response.data;
  };
  
  const _delete = async (path: string) => {
    const response = await apiClient.delete(path, requestConfig);
    return response.data;
  };
  */
  /**
   * Endpoints
   */
  const health = async () => {
    return _get(API_ROUTES.HEALTH);
  };

  const healthDb = async () => {
    return _get(API_ROUTES.HEALTH_DB);
  };

  const getPortfolio = async (): Promise<ICompaniesSummaryResponse> => {
    return _get(`${API_ROUTES.PORTFOLIO}`);
  };

  const getUserLists = async (): Promise<string[]> => {
    return _get(API_ROUTES.GET_USER_LISTS);
  };

  const getPortfolioWithHistory = async (
    ids: string,
    startDate: string,
    endDate: string,
    options: { signal?: AbortSignal } = {}
  ): Promise<ICompanyScoreHistory[]> => {
    const path = `${API_ROUTES.PORTFOLIO_WITH_HISTORY}/true?start_date=${startDate}&end_date=${endDate}&company_codes=${ids}`;
    return _get(path, { signal: options.signal });
  };

  const getCompany = async (companyId: string): Promise<ICompany> => {
    return _get(`${API_ROUTES.COMPANY}/${companyId}`);
  };

  const getArticles = async (
    companyId: string,
    sortCriteria: string,
    startDate: string,
    endDate: string,
    offset: number,
    limit: number = ARTICLES_PER_PAGE,
    filter: string = ''
  ): Promise<IArticlesResponse> => {
    let parameters = `limit=${limit}&offset=${offset}&sort_criteria=${sortCriteria}`;
    if (startDate !== '') {
      parameters = parameters.concat(`&start_date=${startDate}`.toString());
    }
    if (endDate !== '') {
      parameters = parameters.concat(`&end_date=${endDate}`.toString());
    }
    parameters = parameters.concat(`${filter ? `&filter=${filter}` : ''}`.toString());

    return _get(`${API_ROUTES.ARTICLES}/${companyId}?${parameters}`);
  };

  const getArticleWithDowJones = async (dowj_article_id: string): Promise<IArticleWithDowJones> => {
    return _get(`${API_ROUTES.ARTICLE}/${dowj_article_id}`);
  };

  const getThemes = async (
    companyId: string,
    startDate: string,
    endDate: string,
    offset: number,
    limit: number = ARTICLES_PER_PAGE
  ): Promise<IThemesResponse> => {
    let parameters = `limit=${limit}&offset=${offset}`;
    if (startDate !== '') {
      parameters = parameters.concat(`&start_date=${startDate}`);
    }
    if (endDate !== '') {
      parameters = parameters.concat(`&end_date=${endDate}`);
    }
    const themesPath = getThemesPath(companyId);
    return _get(`${themesPath}?${parameters}`);
  };

  const getThemeArticles = async (
    themeId: string,
    companyCode: string,
    offset: number,
    limit: number = ARTICLES_PER_PAGE
  ): Promise<IArticlesResponse> => {
    return _get(`${API_ROUTES.THEME_ARTICLES}?theme_id=${themeId}&company_code=${companyCode}&limit=${limit}&offset=${offset}`);
  };

  // const searchNotInPortfolio = async (term: string): Promise<ICompaniesResponse> => {
  //   return _get(`${API_ROUTES.SEARCH_NOT_IN_PORTFOLIO}?search_term=${encodeURIComponent(term)}`);
  // };

  const searchIncludingPortfolio = async (term: string, usingArticles: boolean, offset: number = 0): Promise<ISearchCompaniesResponse> => {
    if (usingArticles) {
      return _get(`${API_ROUTES.SEARCH_INCLUDING_PORTFOLIO_USING_ARTICLES}?search_term=${encodeURIComponent(term)}&offset=${offset}`);
    }
    return _get(`${API_ROUTES.SEARCH_INCLUDING_PORTFOLIO}?search_term=${encodeURIComponent(term)}&offset=${offset}`);
  };

  const startBulkScore = async (toScoreCompanies: IToScoreCompany[]): Promise<string> => {
    return _post(`${API_ROUTES.START_BULK_SCORE}`, toScoreCompanies);
  };

  const getBulkScore = async (taskID: string): Promise<IScoredCompaniesResponse> => {
    return _get(`${API_ROUTES.GET_BULK_SCORE}/${taskID}`);
  };

  const uploadUserCompany = async (toUploadCompanies: IScoredUpload[]): Promise<IScoredUploadResponse[]> => {
    return _post(`${API_ROUTES.UPLOAD_USER_COMPANY}`, toUploadCompanies);
  };

  const addToPortfolio = async (company_code: string): Promise<null> => {
    return _post(`${API_ROUTES.ADD_TO_PORTFOLIO}/${company_code}`);
  };

  const removeFromPortfolio = async (company_code: string): Promise<null> => {
    return _post(`${API_ROUTES.REMOVE_FROM_PORTFOLIO}/${company_code}`);
  };

  const getContactUsDefaults = async (): Promise<IContactDefaults> => {
    return _get(API_ROUTES.GET_CONTACT_US_DEFAULTS);
  };
  const postContactUs = async (contactUsRequest: IContactRequest): Promise<IContactRequest> => {
    return _post(API_ROUTES.POST_CONTACT_US, contactUsRequest);
  };

  // const downloadPortfolio = async (): Promise<String> => {
  //   return _get(API_ROUTES.DOWNLOAD);
  // };

  const getConfig = async (): Promise<IConfig> => {
    let environment = '';

    // See if there's a better way to do this
    if (window.location.href.includes('fss-lc.owex.oliverwyman.com')) {
      environment = 'dev';
    } else if (window.location.href.includes('fss.owex.oliverwyman.com')) {
      environment = 'prod';
    } else {
      environment = 'local';
    }

    return _get(`${API_ROUTES.CONFIG}?environment=${environment}`);
  };

  const validateUserWithInfo = async (user: UserInfo | undefined): Promise<IValidateUserResponse> => {
    return _post(API_ROUTES.VALIDATE_USER, user);
  };

  const savePrivacyConsent = async (consent: IPrivacyConsentRequest): Promise<null> => {
    return _post(API_ROUTES.PUT_PRIVACY_CONSENT, consent);
  };

  const sendTestEmail = async (): Promise<IEmail> => {
    return _post(API_ROUTES.SEND_TEST_EMAIL);
  };

  const sendEmail = async (): Promise<IEmail> => {
    return _post(API_ROUTES.SEND_EMAIL);
  };

  const saveEmailPreferences = async (
    alertType: AlertTypeKey,
    payload: IDigestAlertPreference | ISmartAlertPreference | ICustomAlertPreference[]
  ) => {
    return _post(`${API_ROUTES.SAVE_EMAIL_PREFERENCES}/${alertType}`, payload);
  };

  const createCustomAlert = async (payload: ICustomAlertPreference): Promise<ICustomAlertId> => {
    return _post(`${API_ROUTES.CREATE_CUSTOM_ALERT}`, payload);
  };

  const updateCustomAlert = async (payload: ICustomAlertPreference): Promise<string> => {
    return _post(`${API_ROUTES.UPDATE_CUSTOM_ALERT}`, payload);
  };

  const deleteCustomAlert = async (payload: ICustomAlertId): Promise<string> => {
    return _post(`${API_ROUTES.DELETE_CUSTOM_ALERT}`, payload);
  };

  const getEmailPreferences = async (): Promise<IUserEmailPreferencesResponse> => {
    return _get(API_ROUTES.GET_EMAIL_PREFERENCES);
  };

  const logout = async () => {
    return _post(API_ROUTES.LOGOUT);
  };

  const login = async () => {
    return _get(API_ROUTES.LOGIN);
  };

  const getAllUsers = async (): Promise<IUserData[]> => {
    return _get(API_ROUTES.GET_ALL_USERS);
  };

  const updateUserEmailPreferences = async (alertType: AlertTypeKey, payload: IUsersEmailPreferences): Promise<any> => {
    return _post(`${API_ROUTES.UPDATE_USER_EMAIL_PREFERENCES}/${alertType}`, payload);
  };

  const createUserList = async (payload: IUserListCreate): Promise<any> => {
    return _post(`${API_ROUTES.CREATE_USER_LIST}`, payload);
  };

  const deleteUserList = async (payload: IUserListDelete): Promise<any> => {
    return _post(`${API_ROUTES.DELETE_USER_LIST}`, payload);
  };

  const addCompanyToList = async (payload: IUserListAddOrRemoveCompany): Promise<any> => {
    return _post(`${API_ROUTES.ADD_COMPANY_TO_LIST}`, payload);
  };

  const removeCompanyFromList = async (payload: IUserListAddOrRemoveCompany): Promise<any> => {
    return _post(`${API_ROUTES.REMOVE_COMPANY_FROM_LIST}`, payload);
  };

  const getMetrics = async (): Promise<IMetricSeries[]> => {
    return _get(API_ROUTES.METRICS);
  };

  const getLoginAttempts = async (): Promise<ILoginAttempts[]> => {
    return _get(API_ROUTES.LOGIN_STATS);
  };

  const getPipelineStatus = async (job_ids: string): Promise<IPipelineStatusResponse> => {
    return _get(`${API_ROUTES.PIPELINE_STATUS}?job_ids=${job_ids}`);
  };

  return {
    ARTICLES_PER_PAGE,
    health,
    healthDb,
    getPortfolio,
    getUserLists,
    getPortfolioWithHistory,
    getCompany,
    getArticles,
    getArticleWithDowJones,
    getThemeArticles,
    // searchNotInPortfolio,
    searchIncludingPortfolio,
    startBulkScore,
    getBulkScore,
    uploadUserCompany,
    addToPortfolio,
    removeFromPortfolio,
    getContactUsDefaults,
    postContactUs,
    getConfig,
    savePrivacyConsent,
    validateUserWithInfo,
    getThemes,
    // downloadPortfolio,
    sendTestEmail,
    sendEmail,
    saveEmailPreferences,
    getEmailPreferences,
    createCustomAlert,
    updateCustomAlert,
    deleteCustomAlert,
    createUserList,
    deleteUserList,
    addCompanyToList,
    removeCompanyFromList,
    logout,
    login,
    getAllUsers,
    updateUserEmailPreferences,
    getMetrics,
    getLoginAttempts,
    getPipelineStatus,
  };
}
